<script>
import { useElixioConnectStore } from '../../../stores/elixioConnect'
import EcTabs from '../../ui/EcTabs.vue'
import EcTabPane from '../../ui/EcTabPane.vue'
import EcButton from '../../ui/EcButton.vue'
import EcErrorBanner from '../../ui/EcErrorBanner.vue'
import ModuleCredentialForm from '../ModuleCredentialForm.vue'
import ModuleAdditionalConfigForm from '../ModuleAdditionalConfigForm.vue'
import ModuleOAuth2Connect from '../ModuleOAuth2Connect.vue'
import ModuleDataBatchSync from '../ModuleDataBatchSync.vue'

const SERVICE_MODE_TEST = 'test'
const SERVICE_MODE_PROD = 'prod'

export default {
  name: 'GenericServiceConfig',
  components: {
    EcTabs, EcTabPane, EcButton, EcErrorBanner,
    ModuleCredentialForm, ModuleAdditionalConfigForm,
    ModuleOAuth2Connect, ModuleDataBatchSync,
  },
  props: {
    service: { type: Object, required: true },
    organization: { type: Object, required: true },
  },
  data() {
    return {
      store: useElixioConnectStore(),
      SERVICE_MODE_TEST,
      SERVICE_MODE_PROD,
      loading: false,
      errors: [],
      serviceData: { ...this.service },
      activeTabMode: (this.service.mode || SERVICE_MODE_TEST),
      activeTab: 'tab-configuration',
      testConfig: this.service.test_config || {},
      prodConfig: this.service.prod_config || {},
    }
  },
  computed: {
    t() {
      return this.$ecTranslate || ((k) => k)
    },
    notify() {
      return this.$ecNotify || (() => {})
    },
    confirm() {
      return this.$ecConfirm || ((msg) => Promise.resolve(window.confirm(msg)))
    },
    credentialsFields() {
      return this.service.fields?.credentials ?? []
    },
    additionalConfigFields() {
      return this.service.fields?.additional_config ?? []
    },
    showSubmitButtons() {
      if (this.serviceData.auth_mode === 'oauth2' && !this.serviceData.is_active) return false
      if (this.activeTab !== 'tab-configuration') return false
      if (this.serviceData.is_active && this.additionalConfigFields.length) return true
      if (
        !this.serviceData.is_active &&
        (this.additionalConfigFields.length || this.credentialsFields.length)
      )
        return true
      return false
    },
  },
  watch: {
    service(val) {
      this.serviceData = { ...val }
    },
  },
  methods: {
    extractErrors(error) {
      const data = error?.response?.data
      if (!data) return [this.t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG')]

      const msgs = []

      const apiErrors = data?.response?.errors ?? data?.errors
      if (Array.isArray(apiErrors) && apiErrors.length) {
        msgs.push(...apiErrors.map((e) => e.detail ?? e.title ?? String(e)))
      }

      if (!msgs.length && data?.response?.detail) {
        msgs.push(data.response.detail)
      }
      if (!msgs.length && data?.response?.title) {
        msgs.push(data.response.title)
      }
      if (!msgs.length && data?.response?.message) {
        msgs.push(data.response.message)
      }
      if (!msgs.length && data?.message) {
        msgs.push(data.message)
      }

      return msgs.length ? msgs : [this.t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG')]
    },
    async toggleServiceActivation(isActive) {
      this.errors = []
      const message = this.serviceData.is_deactivated
        ? this.t('ELIXIO_CONNECT.ELIXIO_CONNECT_CONFIRM_THIS_REACTIVATION')
        : this.t('ELIXIO_CONNECT.ELIXIO_CONNECT_CONFIRM_THIS_DEACTIVATION')

      const confirmed = await this.confirm(message)
      if (!confirmed) return

      this.loading = true
      let success = false

      try {
        await this.store.toggleServiceActivation(this.organization.id, {
          service_name: this.serviceData.name,
          service_type: this.serviceData.slug,
          is_active: isActive,
        })
        this.notify(this.t('ELIXIO_CONNECT.OPERATION_SUCCEEDED'), 'success')
        success = true
        this.serviceData.is_active = isActive
        this.serviceData.is_deactivated = !isActive
      } catch (error) {
        console.error('toggleServiceActivation =>', error)
        this.errors = this.extractErrors(error)
        this.notify(this.errors[0] ?? this.t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG'), 'error')
        this.$nextTick(() => {
          if (this.$refs.configEl) {
            this.$refs.configEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      } finally {
        this.loading = false
        this.$emit('submitted', { success, service: this.serviceData })
      }
    },
    async handleSubmit() {
      const confirmed = await this.confirm(
        this.t('ELIXIO_CONNECT.ELIXIO_CONNECT_CONFIRM_THIS_SUBSCRIPTION'),
      )
      if (!confirmed) return

      if (!this.serviceData.is_active && !this.serviceData.is_deactivated) {
        await this.subscribe()
      } else {
        await this.submitConfig()
      }
    },
    async submitConfig() {
      this.errors = []
      this.loading = true
      let success = false

      try {
        const result = await this.store.updateConfigs(this.organization.id, {
          additional_config: { ...this.serviceData.additional_config },
          service_name: this.serviceData.name,
          service_type: this.serviceData.slug,
          test_config: { ...this.testConfig },
          prod_config: { ...this.prodConfig },
        })
        this.notify(result?.message ?? this.t('ELIXIO_CONNECT.OPERATION_SUCCEEDED'), 'success')
        success = true
      } catch (error) {
        console.error('submitConfig =>', error)
        this.errors = this.extractErrors(error)
        this.notify(this.errors[0] ?? this.t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG'), 'error')
        this.$nextTick(() => {
          if (this.$refs.configEl) {
            this.$refs.configEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      } finally {
        this.loading = false
        this.$emit('submitted', { success, service: this.serviceData })
      }
    },
    async subscribe() {
      this.errors = []
      this.loading = true

      try {
        const result = await this.store.subscribe(this.organization.id, {
          service_name: this.serviceData.name,
          service_type: this.serviceData.slug,
          test_config: { ...this.testConfig },
          prod_config: { ...this.prodConfig },
        })

        if (result.service_data) {
          const serviceId = result.service_data.elixio_service_id ?? null
          this.serviceData.id = serviceId

          if (serviceId) {
            this.$emit('submitted', { success: true, service: this.serviceData })
            this.notify(this.t('ELIXIO_CONNECT.AUTH_FINISHED'), 'success')
          } else {
            this.notify(this.t('ELIXIO_CONNECT.NOT_CONFIGURED_YET'), 'warning')
          }
        }
      } catch (error) {
        console.error('Subscribe =>', error)
        this.errors = this.extractErrors(error)
        this.notify(this.errors[0] ?? this.t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG'), 'error')
        this.$nextTick(() => {
          if (this.$refs.configEl) {
            this.$refs.configEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      } finally {
        this.loading = false
      }
    },
    close() {
      this.$emit('cancelled')
    },
    handleOAuth2Subscribed(payload) {
      this.$emit('submitted', payload)
    },
  },
}
</script>

<template>
  <div ref="configEl" class="ec-service-config">
    <!-- Error banner -->
    <EcErrorBanner v-if="errors.length" :errors="errors" class="ec-mb-3" />

    <!-- Service is active -->
    <div v-if="serviceData.is_active">
      <!-- Deactivate link -->
      <div v-if="!serviceData.is_deactivated" class="ec-service-config__deactivate">
        <button
          class="ec-link ec-link--danger"
          @click="toggleServiceActivation(false)"
        >
          <i class="bi bi-power" />
          {{ t('ELIXIO_CONNECT.DEACTIVATE') }} {{ serviceData.name }}
        </button>
      </div>

      <EcTabs v-model="activeTab">
        <EcTabPane
          v-if="additionalConfigFields.length"
          :label="t('ELIXIO_CONNECT.ELIXIO_ADDITIONNAL_CONFIG_TITLE')"
          name="tab-configuration"
          icon="gear"
        >
          <ModuleAdditionalConfigForm
            v-model="serviceData.additional_config"
            :fields="additionalConfigFields"
            :disabled="loading"
          />
        </EcTabPane>

        <EcTabPane
          v-if="serviceData.batch_synchable"
          label="Batch Sync"
          name="tab-batch-connect"
          icon="arrow-repeat"
        >
          <ModuleDataBatchSync
            :organization="organization"
            :service="service"
          />
        </EcTabPane>

        <!-- Slot for service-specific tabs -->
        <slot name="extra-tabs" :service-data="serviceData" :loading="loading" />
      </EcTabs>
    </div>

    <!-- Service is NOT active -->
    <div v-else>
      <!-- Reactivation -->
      <div v-if="serviceData.is_deactivated" class="ec-service-config__reactivate">
        <button
          class="ec-link ec-link--success"
          @click="toggleServiceActivation(true)"
        >
          <i class="bi bi-power" />
          {{ t('ELIXIO_CONNECT.REACTIVATE') }} {{ serviceData.name }}
        </button>
      </div>

      <!-- Credentials tabs -->
      <div v-if="serviceData.auth_mode !== 'oauth2'">
        <EcTabs v-if="credentialsFields.length" v-model="activeTabMode">
          <EcTabPane
            :label="t('ELIXIO_CONNECT.ELIXIO_CONNECT_TEST_CONFIG')"
            :name="SERVICE_MODE_TEST"
            icon="bug"
          >
            <ModuleCredentialForm
              v-model="testConfig"
              :service-type="serviceData.slug"
              :fields="credentialsFields"
              :disabled="loading"
            />
          </EcTabPane>

          <EcTabPane
            :label="t('ELIXIO_CONNECT.ELIXIO_CONNECT_PROD_CONFIG')"
            :name="SERVICE_MODE_PROD"
            icon="rocket-takeoff"
          >
            <ModuleCredentialForm
              v-model="prodConfig"
              :service-type="serviceData.slug"
              :fields="credentialsFields"
              :disabled="loading"
            />
          </EcTabPane>
        </EcTabs>
      </div>

      <!-- OAuth2 -->
      <ModuleOAuth2Connect
        v-else
        :organization="organization"
        :service="service"
        @subscribed="handleOAuth2Subscribed"
      />

      <!-- Instructions -->
      <div v-if="serviceData.instructions" class="ec-service-config__instructions" v-html="serviceData.instructions" />
    </div>

    <!-- Submit / Close -->
    <div v-if="showSubmitButtons" class="ec-service-config__actions">
      <EcButton variant="outline-secondary" :disabled="loading" @click="close">
        {{ t('ELIXIO_CONNECT.CLOSE') }}
      </EcButton>
      <EcButton
        variant="primary"
        :loading="loading"
        :disabled="loading"
        icon="check-lg"
        @click="handleSubmit"
      >
        {{ t('ELIXIO_CONNECT.ELIXIO_CONNECT_SUBMIT') }}
      </EcButton>
    </div>
  </div>
</template>
